// src/firebase-config.js
import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getAnalytics } from "firebase/analytics";

// Flag to track if we've already attempted initialization
let initialized = false;

// Capture any errors during initialization
let initError = null;

// Firebase instances - use different variable names to avoid redeclaration
let _app = null;
let _auth = null;
let _firestore = null;
let _analytics = null;

function initializeFirebase() {
  // Don't initialize twice
  if (initialized) {
    return { app: _app, auth: _auth, firestore: _firestore, analytics: _analytics, error: initError };
  }

  initialized = true;

  try {
    // Firebase configuration
    const firebaseConfig = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
    };

    // Check required fields
    const missingFields = Object.entries(firebaseConfig)
      .filter(([key, value]) => ['apiKey', 'authDomain', 'projectId'].includes(key) && !value)
      .map(([key]) => key);

    if (missingFields.length > 0) {
      throw new Error(`Missing required Firebase config: ${missingFields.join(', ')}`);
    }

    // Initialize Firebase app
    _app = initializeApp(firebaseConfig);

    // Initialize auth
    _auth = getAuth(_app);

    // getAuth uses Firebase's browser persistence and restores it before notifying
    // onAuthStateChanged. No separate application login flag is needed.

    // Initialize Firestore
    _firestore = getFirestore(_app);

    // Initialize Analytics in browser environments
    if (typeof window !== 'undefined') {
      try {
        _analytics = getAnalytics(_app);
      } catch (analyticsError) {
        console.warn("Firebase analytics initialization failed:", analyticsError);
      }
    }

  } catch (error) {
    initError = error;
    if (process.env.NODE_ENV === 'development') {
      console.error("Firebase initialization failed:", error.message);
    }

    // Surface configuration errors to the UI; fake SDK objects hide failures.
    _auth = null;
    _firestore = null;
  }

  return { app: _app, auth: _auth, firestore: _firestore, analytics: _analytics, error: initError };
}

// Initialize on import
const firebaseInstances = initializeFirebase();

// Export Firebase instances
export const app = firebaseInstances.app;
export const auth = firebaseInstances.auth;
export const firestore = firebaseInstances.firestore;
export const analytics = firebaseInstances.analytics;

// Export initialization function for explicit initialization if needed
export const initFirebase = initializeFirebase;

// Default export
export default {
  app,
  auth,
  firestore,
  analytics,
  initFirebase
};
